import { useCallback, useEffect, useRef, useState } from 'react';
import { exportScene, ExportError, suggestFileName, type ExportedFile } from '@/core/export/export';
import type { Project } from '@/core/project/types';
import type { Scene } from '@/core/render/scene';
import { verifyExport, type DecodeVerdict } from '@/core/validation/decode';

/**
 * Экспорт с проверкой результата.
 *
 * При старте фиксируется снимок сцены и номер ревизии проекта. Если проект
 * успел измениться, устаревший результат не показывается и файл не отдаётся:
 * иначе пользователь мог бы скачать файл от предыдущей строки.
 */

export type ExportJobState =
  | { phase: 'idle' }
  | { phase: 'rendering'; revision: number }
  | { phase: 'verifying'; revision: number }
  | {
      phase: 'ready';
      revision: number;
      file: ExportedFile;
      verdict: DecodeVerdict;
      url: string;
      fileName: string;
    }
  | { phase: 'failed'; revision: number; message: string }
  | { phase: 'cancelled' };

export type ExportJob = {
  state: ExportJobState;
  run: (scene: Scene, project: Project, revision: number) => void;
  cancel: () => void;
  reset: () => void;
};

export function useExportJob(currentRevision: number): ExportJob {
  const [state, setState] = useState<ExportJobState>({ phase: 'idle' });
  const jobId = useRef(0);
  const urlRef = useRef<string | null>(null);

  const releaseUrl = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, []);

  // Освобождаем Blob URL только когда файл уже недоступен для действия.
  useEffect(() => releaseUrl, [releaseUrl]);

  // Любое изменение проекта сбрасывает прошлый успешный результат.
  useEffect(() => {
    setState((prev) => {
      if (prev.phase === 'idle' || prev.phase === 'cancelled') return prev;
      if (prev.revision === currentRevision) return prev;
      releaseUrl();
      return { phase: 'idle' };
    });
  }, [currentRevision, releaseUrl]);

  const run = useCallback(
    (scene: Scene, project: Project, revision: number) => {
      const id = ++jobId.current;
      releaseUrl();
      setState({ phase: 'rendering', revision });

      void (async () => {
        try {
          const file = await exportScene(scene, project.output.format, {
            jpegQuality: project.output.jpegQuality,
          });
          if (jobId.current !== id) return;

          setState({ phase: 'verifying', revision });
          const verdict = await verifyExport(file, scene.meta.payload);
          if (jobId.current !== id) return;

          if (verdict.status === 'mismatch' || verdict.status === 'not-found') {
            setState({
              phase: 'failed',
              revision,
              message:
                verdict.status === 'mismatch'
                  ? 'Проверка прочитала не то содержимое, которое вы ввели. Файл не отдаём — сообщите об этом случае.'
                  : 'Встроенная проверка не нашла QR в готовом файле. Увеличьте размер или упростите оформление.',
            });
            return;
          }

          const url = URL.createObjectURL(file.blob);
          urlRef.current = url;
          setState({
            phase: 'ready',
            revision,
            file,
            verdict,
            url,
            fileName: suggestFileName(file.extension),
          });
        } catch (err) {
          if (jobId.current !== id) return;
          const message =
            err instanceof ExportError
              ? err.message
              : err instanceof Error
                ? err.message
                : 'Не удалось подготовить файл.';
          setState({ phase: 'failed', revision, message });
        }
      })();
    },
    [releaseUrl],
  );

  const cancel = useCallback(() => {
    jobId.current += 1;
    releaseUrl();
    setState({ phase: 'cancelled' });
  }, [releaseUrl]);

  const reset = useCallback(() => {
    jobId.current += 1;
    releaseUrl();
    setState({ phase: 'idle' });
  }, [releaseUrl]);

  return { state, run, cancel, reset };
}
